import axios, { AxiosResponse } from 'axios';

import { getWebpackEnvVar } from '@trqk/utils';

const dashboardBuiltEventOrder = ['pending', 'processing', 'success'];

const bucketName = getWebpackEnvVar('web', 'AWS_S3_LOGS_BUCKET');

interface HandleLogDetailsInput {
    logEvents: LogEvent[];
    handlers: HandlersState;
}

interface HandleLogDetailsResult {
    logEventsWithDetails: LogEvent[];
    eventsToRetry: LogEvent[];
}

interface LogDownloadResult {
    event: LogEvent;
    detail: Record<string, unknown> | null;
    error: Error | null;
}

const throttleMs = 200;

/**
 * For each log event that has one or more handlers:
 * - download log details from S3
 * - add details to log event
 * - call the handler(s) with the event.
 * @param {HandleLogDetailsInput}
 * @returns {LogEvent[]} the logEvents that were passed in. For any event that has a handler, its
 * returned value will be augmented with `detail` fetched from the JSON data stored in S3.
 */
export const handleLogDetails = async ({
    logEvents,
    handlers,
}: HandleLogDetailsInput): Promise<HandleLogDetailsResult> => {
    // For any event that has at least one handler, set up a promise to download the JSON data for
    // that event from S3
    const detailsToDownload = logEvents.filter(
        ({ eventType }: LogEvent): boolean =>
            Object.prototype.hasOwnProperty.call(handlers, eventType),
    );

    const downloadPromises: Promise<LogDownloadResult>[] =
        detailsToDownload.map(
            (event: LogEvent, index: number): Promise<LogDownloadResult> =>
                new Promise(resolve => {
                    const delay = index * throttleMs;
                    const url = `https://${bucketName}.s3.amazonaws.com/${event.key}`;

                    setTimeout((): void => {
                        axios({
                            method: 'get',
                            url,
                            validateStatus: (status: number): boolean =>
                                status === 200,
                        })
                            .then(
                                (
                                    response: AxiosResponse<
                                        Record<string, unknown>
                                    >,
                                ): void => {
                                    const detail = response.data ?? null;

                                    resolve({
                                        event,
                                        detail,
                                        error: null,
                                    });
                                },
                            )
                            .catch((error: Error): void => {
                                resolve({
                                    event,
                                    detail: null,
                                    error,
                                });
                            });
                    }, delay);
                }),
            [] as Promise<LogDownloadResult>[],
        );

    // Start all downloads in parallel; they will fire at the throttleMs rate without blocking
    // each other.
    const downloads = await Promise.allSettled(downloadPromises);
    const eventsToRetry: LogEvent[] = [];

    // Match each completed download to its key
    const detailsByKey = downloads.reduce(
        (
            acc: Record<string, LogDetail>,
            result: PromiseSettledResult<LogDownloadResult>,
        ): Record<string, LogDetail> => {
            // eslint-disable-next-line
            // @ts-ignore
            const key = result?.value?.event?.key ?? null;
            // eslint-disable-next-line
            // @ts-ignore
            const detail: LogDetail = result?.value?.detail ?? null;
            // eslint-disable-next-line
            // @ts-ignore
            const event: LogEvent = result?.value?.event ?? null;

            if (
                result.status === 'fulfilled' &&
                key !== null &&
                detail !== null
            ) {
                acc[key] = detail;
            } else if (event !== null) {
                eventsToRetry.push(event);
            }

            return acc;
        },
        {} as Record<string, LogDetail>,
    );

    // The backend sometimes logs dashboardBuilt events out of order, i.e. `pending` can come
    // after `success`. To show the user the expected order of status messages, we sort
    // dashboardBuilt events for the same statement_id by the expected order in
    // dashboardBuiltEventOrder. This won't work if the events for a given statement_id span across
    // two polling intervals, but that's an edge case.
    logEvents.sort((eventA: LogEvent, eventB: LogEvent): number => {
        if (
            eventA.eventType === 'dashboardBuilt' &&
            eventB.eventType === 'dashboardBuilt'
        ) {
            if (
                (eventA?.detail?.statement_id ?? '0') ===
                (eventB?.detail?.statement_id ?? '1')
            ) {
                const eventAIndex = dashboardBuiltEventOrder.indexOf(
                    eventA.eventStatus,
                );
                const eventBIndex = dashboardBuiltEventOrder.indexOf(
                    eventB.eventStatus,
                );

                if (eventAIndex > -1 && eventBIndex > -1) {
                    if (eventAIndex < eventBIndex) {
                        return -1;
                    }

                    return 1;
                }
            }
        }

        return 0;
    });

    // Add the downloaded detail to the event's .detail property. Also, call the handler with the
    // event that now has its detail.
    const logEventsWithDetails = logEvents.map(
        (logEvent: LogEvent): LogEvent => {
            const detail = detailsByKey?.[logEvent.key] ?? null;

            if (detail !== null) {
                const handlersForEvent = handlers?.[logEvent.eventType] ?? null;

                const eventWithDetail = {
                    ...logEvent,
                    detail,
                };

                if (handlersForEvent !== null) {
                    Object.values(handlersForEvent).forEach(
                        (handler: LogEventHandler): void => {
                            handler(eventWithDetail);
                        },
                    );
                }

                return eventWithDetail;
            }

            return logEvent;
        },
    );

    return {
        logEventsWithDetails,
        eventsToRetry,
    };
};
